import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { FilePlusIcon, FileText } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard, FileName, SimpleMessageCard } from "./shared";
import { getProjectVersionIdFromResult } from "./shared/get-project-version-id";
import { useFileLinkProps } from "./useFileLinkProps";

interface CreateFileProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}

export function CreateFile({ part, messageId }: CreateFileProps) {
  const path = part.args?.path as string;
  const projectId = useActiveProjectId();
  const snapshotId = getProjectVersionIdFromResult(part);
  const linkProps = useFileLinkProps(projectId, messageId, snapshotId, path);
  const fileLink = <FileName filename={path} linkProps={linkProps} />;

  const additionalContent =
    part.status === "pending" && path === "src/index.ts" ? (
      <span className="text-xs text-muted-foreground">
        (This may take a little while)
      </span>
    ) : null;

  const title = (
    <span className="inline-flex items-center gap-1">
      {part.status === "complete" ? "Created" : "Creating"} {fileLink}
    </span>
  );

  // Show expandable card for errors
  if (part.status === "error") {
    return (
      <ExpandableMessageCard
        status={part.status}
        result={part.result}
        icon={FileText}
        title={title}
        className="min-w-0"
        accordionValue="create-file"
        additionalContent={additionalContent}
      />
    );
  }

  // Show simple card for normal states
  return (
    <SimpleMessageCard
      icon={FilePlusIcon}
      status={part.status}
      title={title}
      additionalContent={additionalContent}
      className="min-w-0"
    />
  );
}
